import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { Trans } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { IconButton } from '../IconButton'
import { SANS_SERIF_FONT_FAMILY, SERIF_FONT_FAMILY } from '../styles'

const useStyles = createUseStyles({
  closeIconContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  title: {
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 22,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  message: {
    fontStyle: SANS_SERIF_FONT_FAMILY,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '187%',
    color: 'rgba(54, 52, 50, 0.8)',
    paddingLeft: 30,
  },
  bold: {
    fontWeight: 700,
  },
  resend: {
    paddingTop: 25,
  },
  linkText: {
    fontWeight: 600,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  topMargin: {
    marginTop: 24,
  },
})

type EmailType = 'verification' | 'passwordReset'

interface Props {
  emailType: EmailType
  email: string
  onClose?: () => void
  onBack?: () => void
}

export const EmailVerification: FC<Props> = flowMax(
  addDisplayName('EmailVerification'),
  addProps(({ emailType }) => ({
    isVerification: emailType === 'verification',
  })),
  addNavigate,
  addApi,
  addHandlers({
    onResend:
      ({ api, email }) =>
      () => {
        api.auth.createVerificationEmail({ email, source: 'creatorWeb' })
      },
    onLogin:
      ({ navigate }) =>
      () => {
        navigate('/login', { replace: true })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    email,
    isVerification,
    onClose,
    onBack,
    onLogin,
    onResend,
    classes,
    t,
  }) => (
    <div>
      {onClose && (
        <div className={classes.closeIconContainer}>
          <IconButton name="closeRodeo" onClick={onClose} />
        </div>
      )}
      <h1 className={classes.title}>{t('auth.emailVerification.title')}</h1>
      <div className={classes.message}>
        <Trans
          t={t}
          i18nKey={
            isVerification
              ? 'auth.emailVerification.verifyMessage'
              : 'auth.recoverPassword.emailMessage'
          }
          values={{ email }}
        >
          <span className={classes.bold} />
        </Trans>
        {isVerification ? (
          <>
            <div className={classes.resend}>
              <Trans
                t={t}
                i18nKey={'auth.emailVerification.resendMessage'}
                values={{ email }}
              >
                <button className={classes.linkText} onClick={onResend} />
              </Trans>
            </div>
            <button
              className={cx(classes.linkText, classes.topMargin)}
              onClick={onLogin}
            >
              {t('auth.emailVerification.otherAccount')}
            </button>
          </>
        ) : (
          <div>
            <button
              className={cx(classes.linkText, classes.topMargin)}
              onClick={onBack}
            >
              {t('auth.recoverPassword.backToLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
)
